import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

/**
 * Notification Service
 * Handles email reminder preferences and sending reminder emails
 */

class NotificationService {
  /**
   * Get user's notification settings
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User settings
   */
  async getUserSettings(userId) {
    try {
      const { data, error } = await supabase?.from('user_settings')?.select('*')?.eq('user_id', userId)?.maybeSingle();

      if (error) {
        logger?.error('Failed to fetch user settings', error, { userId });
        throw error;
      }

      // Convert snake_case to camelCase
      if (data) {
        return {
          id: data?.id,
          userId: data?.user_id,
          symptomReminders: data?.symptom_reminders,
          reminderFrequency: data?.reminder_frequency,
          reminderTime: data?.reminder_time,
          medicationReminders: data?.medication_reminders,
          appointmentReminders: data?.appointment_reminders,
          appointmentReminderTime: data?.appointment_reminder_time,
          emailNotifications: data?.email_notifications,
          smsNotifications: data?.sms_notifications,
          pushNotifications: data?.push_notifications,
          emailRemindersEnabled: data?.email_reminders_enabled,
          emailReminderFrequency: data?.email_reminder_frequency,
          emailReminderDays: data?.email_reminder_days,
          checkinReminderFrequency: data?.checkin_reminder_frequency,
          checkinReminderDays: data?.checkin_reminder_days,
          checkinReminderTimeSlots: data?.checkin_reminder_time_slots,
          caregiverAlertEnabled: data?.caregiver_alert_enabled,
          caregiverAlertFrequency: data?.caregiver_alert_frequency,
          caregiverAlertQuietHours: data?.caregiver_alert_quiet_hours,
          supportEmailEnabled: data?.support_email_enabled,
          supportEmailFrequency: data?.support_email_frequency,
          textSize: data?.text_size,
          theme: data?.theme,
          highContrast: data?.high_contrast,
          language: data?.language,
          voiceCommands: data?.voice_commands,
          largeTouchTargets: data?.large_touch_targets,
          reduceMotion: data?.reduce_motion,
          screenReader: data?.screen_reader,
          shareWithProviders: data?.share_with_providers,
          shareForResearch: data?.share_for_research,
          caregiverAccess: data?.caregiver_access,
          twoFactorAuth: data?.two_factor_auth,
          encryptLocalData: data?.encrypt_local_data,
          autoLock: data?.auto_lock,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        };
      }

      return null;
    } catch (error) {
      logger?.error('Error in getUserSettings', error, { userId });
      throw error;
    }
  }

  /**
   * Update user's notification settings
   * @param {string} userId - User ID
   * @param {Object} updates - Settings to update
   * @returns {Promise<Object>} Updated settings
   */
  async updateUserSettings(userId, updates) {
    try {
      // Convert camelCase to snake_case
      const dbUpdates = {};
      if (updates?.symptomReminders !== undefined) dbUpdates.symptom_reminders = updates?.symptomReminders;
      if (updates?.reminderFrequency !== undefined) dbUpdates.reminder_frequency = updates?.reminderFrequency;
      if (updates?.reminderTime !== undefined) dbUpdates.reminder_time = updates?.reminderTime;
      if (updates?.medicationReminders !== undefined) dbUpdates.medication_reminders = updates?.medicationReminders;
      if (updates?.appointmentReminders !== undefined) dbUpdates.appointment_reminders = updates?.appointmentReminders;
      if (updates?.appointmentReminderTime !== undefined) dbUpdates.appointment_reminder_time = updates?.appointmentReminderTime;
      if (updates?.emailNotifications !== undefined) dbUpdates.email_notifications = updates?.emailNotifications;
      if (updates?.smsNotifications !== undefined) dbUpdates.sms_notifications = updates?.smsNotifications;
      if (updates?.pushNotifications !== undefined) dbUpdates.push_notifications = updates?.pushNotifications;
      if (updates?.emailRemindersEnabled !== undefined) dbUpdates.email_reminders_enabled = updates?.emailRemindersEnabled;
      if (updates?.emailReminderFrequency !== undefined) dbUpdates.email_reminder_frequency = updates?.emailReminderFrequency;
      if (updates?.emailReminderDays !== undefined) dbUpdates.email_reminder_days = updates?.emailReminderDays;
      if (updates?.checkinReminderFrequency !== undefined) dbUpdates.checkin_reminder_frequency = updates?.checkinReminderFrequency;
      if (updates?.checkinReminderDays !== undefined) dbUpdates.checkin_reminder_days = updates?.checkinReminderDays;
      if (updates?.checkinReminderTimeSlots !== undefined) dbUpdates.checkin_reminder_time_slots = updates?.checkinReminderTimeSlots;
      if (updates?.caregiverAlertEnabled !== undefined) dbUpdates.caregiver_alert_enabled = updates?.caregiverAlertEnabled;
      if (updates?.caregiverAlertFrequency !== undefined) dbUpdates.caregiver_alert_frequency = updates?.caregiverAlertFrequency;
      if (updates?.caregiverAlertQuietHours !== undefined) dbUpdates.caregiver_alert_quiet_hours = updates?.caregiverAlertQuietHours;
      if (updates?.supportEmailEnabled !== undefined) dbUpdates.support_email_enabled = updates?.supportEmailEnabled;
      if (updates?.supportEmailFrequency !== undefined) dbUpdates.support_email_frequency = updates?.supportEmailFrequency;
      if (updates?.textSize !== undefined) dbUpdates.text_size = updates?.textSize;
      if (updates?.theme !== undefined) dbUpdates.theme = updates?.theme;
      if (updates?.highContrast !== undefined) dbUpdates.high_contrast = updates?.highContrast;
      if (updates?.language !== undefined) dbUpdates.language = updates?.language;
      if (updates?.voiceCommands !== undefined) dbUpdates.voice_commands = updates?.voiceCommands;
      if (updates?.largeTouchTargets !== undefined) dbUpdates.large_touch_targets = updates?.largeTouchTargets;
      if (updates?.reduceMotion !== undefined) dbUpdates.reduce_motion = updates?.reduceMotion;
      if (updates?.screenReader !== undefined) dbUpdates.screen_reader = updates?.screenReader;
      if (updates?.shareWithProviders !== undefined) dbUpdates.share_with_providers = updates?.shareWithProviders;
      if (updates?.shareForResearch !== undefined) dbUpdates.share_for_research = updates?.shareForResearch;
      if (updates?.caregiverAccess !== undefined) dbUpdates.caregiver_access = updates?.caregiverAccess;
      if (updates?.twoFactorAuth !== undefined) dbUpdates.two_factor_auth = updates?.twoFactorAuth;
      if (updates?.encryptLocalData !== undefined) dbUpdates.encrypt_local_data = updates?.encryptLocalData;
      if (updates?.autoLock !== undefined) dbUpdates.auto_lock = updates?.autoLock;

      dbUpdates.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('user_settings')?.update(dbUpdates)?.eq('user_id', userId)?.select()?.single();

      if (error) {
        logger?.error('Failed to update user settings', error, { userId });
        throw error;
      }

      logger?.audit('USER_SETTINGS_UPDATED', userId, { fields: Object.keys(updates) });
      return this.getUserSettings(userId);
    } catch (error) {
      logger?.error('Error in updateUserSettings', error, { userId });
      throw error;
    }
  }

  /**
   * Send daily check-in reminder email
   * @param {string} userEmail - User's email address
   * @param {string} userName - User's name
   * @param {string} reminderTime - Reminder time
   * @returns {Promise<Object>} Response from edge function
   */
  async sendDailyCheckInReminder(userEmail, userName, reminderTime) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-daily-checkin-reminder', {
        body: {
          userEmail,
          userName,
          reminderTime
        }
      });

      if (error) {
        logger?.error('Failed to send daily check-in reminder', error, { userEmail });
        throw error;
      }

      logger?.audit('DAILY_CHECKIN_REMINDER_SENT', null, { userEmail });
      return data;
    } catch (error) {
      logger?.error('Error in sendDailyCheckInReminder', error, { userEmail });
      throw error;
    }
  }

  /**
   * Test send reminder email (for immediate testing)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response from edge function
   */
  async testSendReminder(userId) {
    try {
      // Get user profile
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, first_name, last_name')?.eq('id', userId)?.single();

      if (profileError) {
        logger?.error('Failed to fetch user profile for test reminder', profileError, { userId });
        throw profileError;
      }

      const userName = `${profile?.first_name || ''} ${profile?.last_name || ''}`?.trim() || 'there';

      // Get user settings
      const settings = await this.getUserSettings(userId);
      const reminderTime = settings?.reminderTime || '09:00';

      // Send test email
      return await this.sendDailyCheckInReminder(profile?.email, userName, reminderTime);
    } catch (error) {
      logger?.error('Error in testSendReminder', error, { userId });
      throw error;
    }
  }

  /**
   * Show in-app notification for audit events
   * @param {Object} auditEvent - Audit event data
   */
  showAuditNotification(auditEvent) {
    const { record } = auditEvent;
    
    let title = '';
    let message = '';
    let type = 'info';

    switch (record?.actionType) {
      case 'CREATE':
        title = 'New Entry Created';
        message = `A new ${this.formatResourceType(record?.resourceType)} was created`;
        type = 'success';
        break;
      case 'UPDATE':
        title = 'Data Modified';
        message = `${this.formatResourceType(record?.resourceType)} was updated`;
        type = 'warning';
        break;
      case 'DELETE':
        title = 'Data Deleted';
        message = `${this.formatResourceType(record?.resourceType)} was deleted`;
        type = 'error';
        break;
      case 'VIEW':
        title = 'Data Accessed';
        message = `${this.formatResourceType(record?.resourceType)} was viewed`;
        type = 'info';
        break;
      case 'EXPORT':
        title = 'Data Exported';
        message = `${this.formatResourceType(record?.resourceType)} was exported`;
        type = 'info';
        break;
      default:
        title = 'Audit Event';
        message = 'An audit event occurred';
        type = 'info';
    }

    // Create notification object
    const notification = {
      id: record?.id,
      title,
      message,
      type,
      timestamp: record?.createdAt || new Date()?.toISOString(),
      data: record
    };

    // Store in session storage for persistence
    this.storeNotification(notification);

    // Trigger browser notification if permitted
    this.showBrowserNotification(title, message);

    return notification;
  }

  /**
   * Format resource type for display
   * @param {string} resourceType - Resource type
   * @returns {string} Formatted resource type
   */
  formatResourceType(resourceType) {
    const typeMap = {
      'SYMPTOM_ENTRY': 'Symptom Entry',
      'MANAGED_PROFILE': 'Managed Profile',
      'USER_PROFILE': 'User Profile',
      'CAREGIVER_ALERT': 'Caregiver Alert'
    };
    return typeMap?.[resourceType] || resourceType;
  }

  /**
   * Store notification in session storage
   * @param {Object} notification - Notification object
   */
  storeNotification(notification) {
    try {
      const stored = JSON.parse(sessionStorage.getItem('audit_notifications') || '[]');
      stored?.unshift(notification);
      
      // Keep only last 50 notifications
      const limited = stored?.slice(0, 50);
      sessionStorage.setItem('audit_notifications', JSON.stringify(limited));
    } catch (error) {
      logger?.error('Failed to store notification', error);
    }
  }

  /**
   * Get stored notifications
   * @returns {Array} Array of notifications
   */
  getStoredNotifications() {
    try {
      return JSON.parse(sessionStorage.getItem('audit_notifications') || '[]');
    } catch (error) {
      logger?.error('Failed to get stored notifications', error);
      return [];
    }
  }

  /**
   * Clear stored notifications
   */
  clearStoredNotifications() {
    try {
      sessionStorage.removeItem('audit_notifications');
    } catch (error) {
      logger?.error('Failed to clear notifications', error);
    }
  }

  /**
   * Show browser notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   */
  async showBrowserNotification(title, message) {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        return;
      }

      // Request permission if not granted
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      // Show notification if permitted
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'audit-notification',
          requireInteraction: false
        });
      }
    } catch (error) {
      logger?.error('Failed to show browser notification', error);
    }
  }

  /**
   * Request notification permission
   * @returns {Promise<string>} Permission status
   */
  async requestNotificationPermission() {
    try {
      if (!('Notification' in window)) {
        return 'denied';
      }

      if (Notification.permission === 'default') {
        return await Notification.requestPermission();
      }

      return Notification.permission;
    } catch (error) {
      logger?.error('Failed to request notification permission', error);
      return 'denied';
    }
  }
}

export default new NotificationService();